import * as React from 'react';
import moment from 'moment';
import propz from 'propz';
import classNames from 'classnames';
import { AppUser } from 'Src/views/App/App';
import { Button } from 'Src/components/Button/Button';
import { EventInvitationMessage } from 'Src/models/message';
import { CLUB_MESSAGE_INVITE_STATUS_ALL, CLUB_MESSAGE_INVITE_STATUS_SERVER_TO_CLIENT_MAPPING } from 'Src/consts/club';
import { INVITE_TYPE } from 'Src/consts/invite';
import { SECONDS_IN_MINUTE, MILLISECONDS_IN_SECOND } from 'Src/consts/common';
import { WEEK_DAYS_SERVER_TO_CLIENT_MAPPING } from 'Src/consts/date';
import { getFullName } from 'Src/helpers/autocomplete/autocomplete';
import { formatAgesCompactForSchool } from 'Src/helpers/ageGroups';
import { CLUB_GENDER_SERVER_TO_CLIENT_MAPPING } from 'Src/consts/club';
import { wasEditedAfterResponse, statusChangedAfterFirstResponse } from 'Src/helpers/message/message';
import './ClubBookingCard.scss';

interface Props {
  user: AppUser;
  type: string;
  item: EventInvitationMessage;
  onViewMapClick: (item: EventInvitationMessage) => void;
  onShowDetailsClick: (item: EventInvitationMessage) => void;
  onDeclineClick?: (item: EventInvitationMessage) => void;
  onBookClick?: (item: EventInvitationMessage) => void;
  onReturnToInboxClick?: (item: EventInvitationMessage) => void;
  onEditClick?: (item: EventInvitationMessage) => void;
}

type CloseReason = 'deadline' | 'outdated' | null;

const isClubOutdated = (item: EventInvitationMessage): boolean => {
  const values: any[] = [propz.get(item, ['clubData', 'finishDate'])];
  const dates = propz.get(item, ['clubData', 'dates'], []);
  const intervals = propz.get(item, ['clubData', 'intervals'], []);
  if (Array.isArray(dates)) values.push(...dates);
  if (Array.isArray(intervals)) values.push(...intervals.map(interval => propz.get(interval, ['end'])));
  const ends = values.map(value => moment(value).endOf('day')).filter(value => value.isValid());
  if (!ends.length) return false;
  return moment().isAfter(moment.max(ends));
};

const toFirstSessionMoment = (item: EventInvitationMessage): moment.Moment | null => {
  const startDate = propz.get(item, ['clubData', 'startDate']);
  if (!startDate) return null;

  let m = moment(startDate).startOf('day');
  if (!m.isValid()) return null;

  const timeISO = propz.get(item, ['clubData', 'time']);
  if (timeISO) {
    const t = moment(timeISO);
    if (t.isValid()) {
      m = m
        .hour(t.hour())
        .minute(t.minute())
        .second(0)
        .millisecond(0);
    }
  }

  const days: string[] = propz.get(item, ['clubData', 'days'], []);
  if (Array.isArray(days) && days.length) {
    const wanted = new Set(days.map(d => String(d).toUpperCase()));
    for (let i = 0; i < 7; i++) {
      const dow = m.format('dddd').toUpperCase();
      if (wanted.has(dow)) break;
      m = m.add(1, 'day');
    }
  }

  return m;
};

const formatDMY = (v: any) => {
  const m = moment(v);
  return m.isValid() ? m.format('DD/MM/YYYY') : '';
};

export const isClubBookingClosed = (item: EventInvitationMessage): { closed: boolean; reason: CloseReason } => {
  const deadlineISO = propz.get(item, ['deadlineForAnswers']);
  const outdated = isClubOutdated(item);

  if (deadlineISO) {
    const deadline = moment(deadlineISO);
    if (deadline.isValid()) {
      return moment().isSameOrAfter(deadline)
        ? { closed: true, reason: 'deadline' }
        : { closed: false, reason: null };
    }
  }

  if (outdated) return { closed: true, reason: 'outdated' };

  return { closed: false, reason: null };
};

export const ClubBookingCard = (props: Props) => {
  const {
    user,
    item,
    type,
    onDeclineClick,
    onBookClick,
    onViewMapClick,
    onShowDetailsClick,
    onReturnToInboxClick,
    onEditClick
  } = props;

  const isInbox = type === INVITE_TYPE.INBOX;

  const studentName = getFullName(propz.get(item, ['playerDetailsData']));
  const initials = (studentName || '')
    .split(' ')
    .filter(Boolean)
    .map(s => s[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  const schoolLogo = propz.get(item, ['schoolData', 'pic'], '');
  const clubName = propz.get(item, ['clubData', 'name'], '');

  const sportName = propz.get(item, ['clubData', 'sport', 'name']) || '';

  const postcode = propz.get(item, ['clubData', 'venue', 'placeData', 'postcode'], '');
  const placeName = propz.get(item, ['clubData', 'venue', 'placeData', 'name'], '');
  let venueText = `${postcode}, ${placeName}`.trim();

  if (!postcode && !placeName) {
    const placeNameFlat = propz.get(item, ['clubData', 'venue', 'placeName'], '');
    venueText = placeNameFlat || '';
  }

  const locationStrRaw = (propz.get(item, ['clubData', 'location'], '') || '').trim();
  const hasLocation = locationStrRaw.length > 0;

  const startDate = propz.get(item, ['clubData', 'startDate']);
  const endDate = propz.get(item, ['clubData', 'finishDate']);

  const start = moment(startDate);
  const end = moment(endDate);
  const sameDay = start.isValid() && end.isValid() && start.isSame(end, 'day');

  const startTimeISO = propz.get(item, ['clubData', 'time']);
  const durationMin = propz.get(item, ['clubData', 'duration'], 0);
  const startTs = startTimeISO ? new Date(startTimeISO).getTime() : undefined;
  const endTs = startTs ? startTs + durationMin * SECONDS_IN_MINUTE * MILLISECONDS_IN_SECOND : undefined;

  const prettyDate = (m: moment.Moment) => m.format('ddd D MMM YYYY');
  const prettyTime = (iso?: number) => (iso ? moment(new Date(iso).toISOString()).format('HH:mm') : '');

  const timeRangeSameDay = `${prettyTime(startTs)} – ${prettyTime(endTs)}`;
  const timeRangeMulti = `${prettyTime(startTs)} – ${prettyTime(endTs)}`;

  const weekDays = propz.get(item, ['clubData', 'days'], []) as string[];
  const weekDaysText = (Array.isArray(weekDays) ? weekDays : [])
    .map(d => WEEK_DAYS_SERVER_TO_CLIENT_MAPPING[d] || d)
    .join(', ');
  const deadlineISO = propz.get(item, ['deadlineForAnswers'], '');
  const hasDeadline = !!deadlineISO;

  const deadlineM = hasDeadline && moment(deadlineISO).isValid() ? moment(deadlineISO) : null;

  const nowM = moment();
  const deadlinePassed = !!deadlineM && nowM.isSameOrAfter(deadlineM);

  const hoursLeft =
    deadlineM && !deadlinePassed ? Math.max(0, Math.ceil(deadlineM.diff(nowM, 'minutes') / 60)) : undefined;

  const rosetteText = hasDeadline && deadlineM ? `${deadlineM.format('DD MMM')}\n${deadlineM.format('HH:mm')}` : '';

  const invitationStatus = propz.get(item, ['invitationStatus']);
  const statusCode = invitationStatus;

  const invitationStatusFormatted = CLUB_MESSAGE_INVITE_STATUS_SERVER_TO_CLIENT_MAPPING[invitationStatus];

  const isInvitationStatusWaitingListCanBook = statusCode === CLUB_MESSAGE_INVITE_STATUS_ALL.WAITING_LIST_CAN_BOOK;
  const isInvitationStatusPrebooked = statusCode === CLUB_MESSAGE_INVITE_STATUS_ALL.PREBOOKED;
  const isInvitationStatusAuthorising = statusCode === CLUB_MESSAGE_INVITE_STATUS_ALL.AUTHORISING;

  const statusTextRaw = invitationStatusFormatted || '';

  const codedStatus =
    statusCode === CLUB_MESSAGE_INVITE_STATUS_ALL.ACCEPTED
      ? 'ACCEPTED'
      : statusCode === CLUB_MESSAGE_INVITE_STATUS_ALL.REJECTED
      ? 'REJECTED'
      : 'NOT_READY';

  const { closed: isClosedByRules, reason: closeReason } = isClubBookingClosed(item);
  const isOutdated = closeReason === 'outdated';

  const displayStatusText =
    codedStatus === 'NOT_READY'
      ? isOutdated
        ? 'Outdated'
        : deadlinePassed
        ? 'Booking closed'
        : 'Available for booking'
      : statusTextRaw;

  const statusClass =
    codedStatus === 'NOT_READY'
      ? isOutdated
        ? 'is-outdated'
        : deadlinePassed
        ? 'is-overdue'
        : 'is-awaiting'
      : codedStatus === 'ACCEPTED'
      ? 'is-accepted'
      : codedStatus === 'REJECTED'
      ? 'is-declined'
      : '';

  const gender = propz.get(item, ['clubData', 'gender'], '');
  const genderLabel = CLUB_GENDER_SERVER_TO_CLIENT_MAPPING[gender] || String(gender || '').replace(/_/g, ' ');
  const ageSchool = { ...propz.get(user, ['activeSchool'], {}), ...propz.get(item, ['schoolData'], {}) };
  const agesLabel = formatAgesCompactForSchool(propz.get(item, ['clubData', 'ages'], []), ageSchool);
  const wasUpdated = wasEditedAfterResponse(item) || statusChangedAfterFirstResponse(item);
  const isPaidFlow = Boolean(propz.get(item, ['clubData', 'paymentSettings', 'askToPay'], false));
  const specificDates = (propz.get(item, ['clubData', 'dates'], []) || []) as string[];
  const intervals = (propz.get(item, ['clubData', 'intervals'], []) || []) as Array<{ start: string; end: string }>;
  const scheduleType = String(propz.get(item, ['clubData', 'scheduleType'], '') || '').toUpperCase();
  const isSpecificDates = scheduleType === 'DATES' || scheduleType === 'DATES_LIST' || specificDates.length > 0;
  const orderedDates = specificDates.slice().filter(date => moment(date).isValid()).sort((a, b) => moment(a).valueOf() - moment(b).valueOf());
  const scheduleLabel = isSpecificDates
    ? `${orderedDates.length} specific ${orderedDates.length === 1 ? 'date' : 'dates'}`
    : intervals.length > 1
    ? `${intervals.length} date intervals`
    : Number(propz.get(item, ['clubData', 'repeatEveryWeeks'], 1)) === 2
    ? 'Every other week'
    : 'Every week';
  const datesPreview = isSpecificDates
    ? orderedDates.slice(0, 3).map(date => moment(date).format('D MMM')).join(', ') + (orderedDates.length > 3 ? ` +${orderedDates.length - 3}` : '')
    : sameDay ? formatDMY(start) : `${formatDMY(start)}${end.isValid() ? ` – ${formatDMY(end)}` : ''}`;

  const renderInboxActions = () =>
    !deadlinePassed && !isOutdated ? (
      <div className="ConsentCard__actions">
        <Button
          text="Decline"
          onClick={() => onDeclineClick && onDeclineClick(item)}
          customClass="btn-respond btn-decline"
        />
        <Button
          text={
            statusCode === CLUB_MESSAGE_INVITE_STATUS_ALL.PREBOOKED ||
            statusCode === CLUB_MESSAGE_INVITE_STATUS_ALL.AUTHORISING
              ? 'Info'
              : 'Book'
          }
          onClick={() => onBookClick && onBookClick(item)}
          customClass="btn-respond btn-accept"
        />
      </div>
    ) : (
      <div className="ConsentCard__deadlineBanner">
        {isOutdated ? 'Outdated.' : hasDeadline ? 'Deadline has passed.' : 'Booking closed.'}
      </div>
    );

  const renderArchiveActions = () => {
    const { closed } = isClubBookingClosed(item);
    const canReturn =
      typeof onReturnToInboxClick === 'function' && statusCode === CLUB_MESSAGE_INVITE_STATUS_ALL.REJECTED && !closed;

    if (!canReturn) return null;

    return (
      <div className="ConsentCard__actions">
        <Button
          text="Return to inbox"
          onClick={() => onReturnToInboxClick && onReturnToInboxClick(item)}
          customClass="btn-respond btn-accept"
        />
      </div>
    );
  };

  const cardClasses = classNames('ConsentCard', 'bClubBookingCard', {
    bClubBookingCardHighlighted: statusCode === CLUB_MESSAGE_INVITE_STATUS_ALL.WAITING_LIST_CAN_BOOK
  });

  return (
    <article className={cardClasses}>
      {hasDeadline && (
        <div
          className={[
            'ConsentCard__rosette',
            deadlinePassed ? 'is-past' : hoursLeft !== undefined && hoursLeft <= 48 ? 'is-soon' : ''
          ].join(' ')}
          title={`Deadline: ${moment(deadlineISO).format('DD MMM YYYY, HH:mm')}`}
        >
          <span>DEADLINE</span>
          <strong>{rosetteText}</strong>
        </div>
      )}

      <header className="clubInviteCard__header">
        <div className="ConsentCard__person">
          <div className="ConsentCard__avatar" aria-hidden="true">
            {schoolLogo ? <img src={schoolLogo} alt="" /> : initials || '•'}
          </div>
          <div className="ConsentCard__who">
            <div className="ConsentCard__student">{studentName}</div>
            <div className={`ConsentCard__status ${statusClass}`}>{displayStatusText}</div>
          </div>
        </div>
        <h3 className="ConsentCard__title clamp-2" title={clubName}>
          {clubName}
        </h3>
        <div className="clubInviteCard__chips">
          {sportName ? <span>{sportName}</span> : null}
          <span>{scheduleLabel}</span>
          {genderLabel ? <span>{genderLabel}</span> : null}
          {agesLabel ? <span>{agesLabel}</span> : null}
          {wasUpdated ? <span className="is-updated">Edited response</span> : null}
        </div>
      </header>

      <div className="clubInviteCard__facts">
        <div><span>Dates</span><strong>{datesPreview || 'To be confirmed'}</strong></div>
        <div><span>Sessions</span><strong>{isSpecificDates ? 'Selected days' : weekDaysText || 'To be confirmed'}{timeRangeMulti ? ` · ${timeRangeMulti}` : ''}</strong></div>
        <div><span>Location</span><strong>{hasLocation ? locationStrRaw : venueText || 'To be confirmed'}</strong></div>
      </div>

      {venueText ? <button type="button" className="clubInviteCard__map" onClick={() => onViewMapClick(item)}>
        View venue on map
      </button> : null}

      {hasDeadline && !deadlinePassed ? (
        <div className="clubInviteCard__deadline">
          <span>Reply by</span><strong>{moment(deadlineISO).format('D MMM YYYY, HH:mm')}</strong>
          {typeof hoursLeft === 'number' && hoursLeft <= 48 ? <em>{hoursLeft}h left</em> : null}
        </div>
      ) : null}

      <footer className="clubInviteCard__footer">
        {!isInbox ? <button type="button" className="clubInviteCard__details" onClick={() => onShowDetailsClick(item)}>View details</button> : null}
        <div className="clubInviteCard__primaryActions">
          {!isInbox && codedStatus === 'ACCEPTED' && typeof onEditClick === 'function' && !isClosedByRules && !isPaidFlow ? (
            <button type="button" className="clubInviteCard__edit" onClick={() => onEditClick(item)}>Edit response</button>
          ) : null}
          {isInbox ? renderInboxActions() : renderArchiveActions()}
        </div>
      </footer>
    </article>
  );
};

ClubBookingCard.displayName = 'ClubBookingCard';




